import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import styles from './PowerUpCardWithCheckboxes.module.css';

import { Checkbox } from './Checkbox';
import { WellnessCoverage } from './WellnessCoverage';
import { AddedPill } from './AddedPill';
import { currencyFormat } from '../../../../services/utils';
import { HtmlContent } from '../../../common/HtmlContent';

export const COVERAGE_DEFAULTS = {
  first: 0,
  last: -1,
};

export const PowerUpCardWithCheckboxes = ({
  bundleModifierAdd = () => { },
  bundleModifierCheckSelection = () => { },
  calculatePrice = () => { },
  coverageDefault = COVERAGE_DEFAULTS.first,
  error = false,
  handleAddPowerup,
  handleRemovePowerup,
  multiModalData,
  petQuoteSelected,
  petType,
  powerUpInfo,
  selected = null,
  title = '',
}) => {
  const { t } = useTranslation('changeCoverage');
  const [isMounted, setMounted] = useState(false);
  const [localSelected, setLocalSelected] = useState(selected);
  const [coverageSelected, setCoverageSelected] = useState(null);
  const dispatch = useDispatch();

  const coverageOptions = useMemo(
    () => powerUpInfo.BundleInsuranceModifiers
      ?.filter((modifier) => modifier.IsVisible && modifier.IsActive),
    [powerUpInfo],
  );

  const isMultipleWellness = useMemo(
    () => coverageOptions?.length >= 2,
    [coverageOptions],
  );

  useEffect(() => {
    if (coverageOptions?.length && !coverageSelected) {
      setCoverageSelected(coverageOptions.at(coverageDefault)?.Id);
    }
  }, [coverageOptions, coverageSelected]);

  useEffect(() => {
    setLocalSelected(selected);
  }, [petQuoteSelected, selected]);

  useEffect(() => {
    if (isMultipleWellness) {
      if (selected) {
        const modifierOpt = coverageOptions
          .find((bundleItem) => bundleItem.IsSelected);

        setCoverageSelected(modifierOpt.Id);
      } else {
        setCoverageSelected(coverageOptions?.at?.(coverageDefault)?.Id);
      }
    }
  }, [petQuoteSelected]);

  useEffect(() => {
    if (!isMounted) {
      setMounted(true);
    }
  }, [isMounted]);

  const handleAddPowerupCallback = useCallback((event) => {
    if (!localSelected && handleAddPowerup) {
      handleAddPowerup(event);
    }

    if (error) {
      // WIP prop103: error?
    }
  }, [dispatch, error, handleAddPowerup, localSelected, powerUpInfo.Id]);

  const handleRemovePowerupCallback = useCallback((event) => {
    if ((localSelected || localSelected === null) && handleRemovePowerup) {
      handleRemovePowerup(event);
    }
    if (error) {
      // WIP prop103:
      // dispatch(removePowerupIdError(powerUpInfo.Id));
    }
  }, [dispatch, error, handleRemovePowerup, localSelected, powerUpInfo.Id]);

  const selectedBundle = useMemo(() => {
    if (powerUpInfo?.BundleInsuranceModifiers?.length > 1
      && coverageSelected) {
      return powerUpInfo.BundleInsuranceModifiers
        .find((item) => item.Id === coverageSelected);
    }
    return null;
  }, [powerUpInfo, coverageSelected]);

  const bundleSelected = useMemo(
    () => powerUpInfo?.BundleInsuranceModifiers
      ?.find((modifier) => modifier.Id === coverageSelected),
    [coverageSelected, powerUpInfo],
  );

  const handleBundleSelectionChanged = useCallback(() => {
    bundleModifierAdd(
      {
        isSelected: !bundleModifierCheckSelection?.(coverageSelected),
        powerUp: bundleSelected,
      },
    );
  }, [
    bundleModifierAdd,
    bundleModifierCheckSelection,
    bundleSelected,
    coverageSelected,
  ]);

  const handleOnChangePowerup = useCallback((event) => {
    if (localSelected) {
      handleRemovePowerupCallback(event);
    } else if (isMultipleWellness) {
      handleBundleSelectionChanged();
    } else {
      handleAddPowerupCallback(event);
    }
  }, [
    handleAddPowerupCallback,
    handleBundleSelectionChanged,
    handleRemovePowerupCallback,
    localSelected,
    isMultipleWellness,
  ]);

  useEffect(() => {
    if (localSelected && isMultipleWellness) {
      const modifierOpt = powerUpInfo.BundleInsuranceModifiers
        .find((bundleItem) => bundleItem.IsSelected);
      if (modifierOpt.Id !== coverageSelected) {
        handleBundleSelectionChanged();
      }
    }
  }, [coverageSelected]);

  const price = useMemo(() => {
    if (powerUpInfo?.BundleInsuranceModifiers?.length) {
      return powerUpInfo.BundleInsuranceModifiers
        .find((item) => item.Id === coverageSelected)?.FeeAmount;
    }
    return calculatePrice(powerUpInfo);
  }, [calculatePrice, coverageSelected, powerUpInfo]);

  const titleToDisplay =
    useMemo(() => `${selectedBundle?.TitleText || ''} ${title}`.toUpperCase(),
      [selectedBundle, title]);

  const descriptionToDisplay =
    useMemo(() => selectedBundle?.InputText || powerUpInfo.InputText || '',
      [powerUpInfo, selectedBundle]);

  return (
    <>
      <h3 className={styles.title}>{t('powerUpsSection.title')}</h3>

      <p className={styles.description}>{t('powerUpsSection.description')}</p>

      <section
        aria-labelledby={`Powerup-card-title-${powerUpInfo.Id}`}
        className={localSelected ? styles.containerSelected : styles.container}
        data-error={error}
        id={`Powerup-card-section-${powerUpInfo.Id}`}
      >
        <div className={styles.bodyContent}>
          <div>
            <Checkbox
              checked={localSelected}
              className={styles.checkbox}
              id={`Powerup-card-title-${powerUpInfo.Id}`}
              onClick={handleOnChangePowerup}
            >
              <span>{titleToDisplay}</span>

              <span>
                {t('powerUpsSection.paymentCycle.monthlyPrice', {
                  value: currencyFormat({ value: price }),
                })}
              </span>
            </Checkbox>
          </div>

          {localSelected && (
            <AddedPill text={t('powerUpsSection.added')} />
          )}

          <div className={styles.content}>
            <HtmlContent
              classNameContainer={styles.text}
              content={descriptionToDisplay}
              elementId={`${powerUpInfo.Id}-description`}
              elementType="p"
              shouldRefresh
            />

            {coverageOptions?.length > 0
              && (
                <WellnessCoverage
                  bundleSelected={bundleSelected}
                  coverageSelected={coverageSelected}
                  multiModalDataList={multiModalData}
                  petType={petType}
                  powerup={powerUpInfo}
                  setCoverageSelected={setCoverageSelected}
                />
              )}
          </div>
        </div>
      </section>
    </>
  );
};
